import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import {
  Loader2, Plus, Eye, SkipForward, RotateCcw, Trash2, X, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const CA_SPRINT_ORDER = [
  "Sacramento", "Stockton", "Fresno", "San Jose", "San Francisco",
  "Oakland", "San Diego", "Bakersfield", "Modesto", "Los Angeles",
];

export default function CityManagementPanel() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ city_name: "", state_code: "", population_density_rank: 999 });
  const [detailCity, setDetailCity] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState("");

  const fetchCities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline/cities`);
      setCities(res.data.cities || []);
    } catch { setCities([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  const handleAdd = async () => {
    if (!addForm.city_name || !addForm.state_code) return;
    setActionLoading("add");
    try {
      await axios.post(`${API}/admin/pipeline/cities/add`, addForm);
      toast.success(`Added ${addForm.city_name}, ${addForm.state_code}`);
      setShowAddForm(false);
      setAddForm({ city_name: "", state_code: "", population_density_rank: 999 });
      fetchCities();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add city");
    } finally { setActionLoading(""); }
  };

  const handleAction = async (action, cityId) => {
    setActionLoading(action);
    try {
      if (action === "force-advance") {
        await axios.post(`${API}/admin/pipeline/force-advance`, { city_id: cityId });
        toast.success(`Force advanced ${cityId}`);
      } else if (action === "reset") {
        await axios.post(`${API}/admin/pipeline/reset-city-checkpoints`, { city_id: cityId });
        toast.success(`Reset checkpoints for ${cityId}`);
      } else if (action === "remove") {
        await axios.post(`${API}/admin/pipeline/cities/${cityId}/remove`);
        toast.success(`Removed ${cityId}`);
      }
      setConfirmAction(null);
      fetchCities();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Action failed: ${action}`);
    } finally { setActionLoading(""); }
  };

  const viewDetails = async (cityId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pipeline/cities/${cityId}/details`);
      setDetailCity(res.data);
    } catch {
      toast.error("Failed to load city details");
    } finally { setDetailLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;

  const caSprint = cities.filter(c => c.state_code === "CA" && CA_SPRINT_ORDER.includes(c.city_name))
    .sort((a, b) => CA_SPRINT_ORDER.indexOf(a.city_name) - CA_SPRINT_ORDER.indexOf(b.city_name));

  return (
    <div className="space-y-5" data-testid="city-management-panel">
      {/* California Sprint */}
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4" data-testid="ca-sprint-section">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />California Sprint — Priority Cities
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {CA_SPRINT_ORDER.map((name, idx) => {
            const city = caSprint.find(c => c.city_name === name);
            const phase = city?.current_phase || 0;
            const score = city?.confidence_report?.overall_score || 0;
            const complete = city?.confidence_report?.city_complete || false;
            return (
              <div key={name} className={`rounded-lg border p-2 text-center text-[11px] ${
                complete ? "bg-emerald-50 border-emerald-300" :
                phase > 0 ? "bg-sky-50 border-sky-200" :
                "bg-white border-slate-200"
              }`} data-testid={`ca-city-${idx}`}>
                <p className="font-bold text-slate-700">#{idx + 1} {name}</p>
                <p className="text-slate-500">P{phase} — {score.toFixed(2)}</p>
                {complete && <p className="text-emerald-600 font-bold">Done</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add City Form */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
          data-testid="add-city-toggle-btn"
        >
          <Plus className="w-3 h-3" />{showAddForm ? "Cancel" : "Add City"}
        </button>
      </div>

      {showAddForm && (
        <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50" data-testid="add-city-form">
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text" value={addForm.city_name} onChange={(e) => setAddForm(p => ({ ...p, city_name: e.target.value }))}
              placeholder="City Name" className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="add-city-name"
            />
            <input
              type="text" value={addForm.state_code} onChange={(e) => setAddForm(p => ({ ...p, state_code: e.target.value }))}
              placeholder="State (e.g. CA)" className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="add-city-state"
            />
            <input
              type="number" value={addForm.population_density_rank} onChange={(e) => setAddForm(p => ({ ...p, population_density_rank: parseInt(e.target.value) || 999 }))}
              placeholder="Pop. Rank" className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="add-city-rank"
            />
          </div>
          <button
            onClick={handleAdd} disabled={!addForm.city_name || !addForm.state_code || !!actionLoading}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="add-city-submit-btn"
          >
            {actionLoading === "add" ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Plus className="w-3 h-3 inline mr-1" />}
            Add to Pipeline
          </button>
        </div>
      )}

      {/* Cities Table */}
      <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="cities-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                <th className="text-left py-2 px-3">City</th>
                <th className="text-center py-2 px-2">Phase</th>
                <th className="text-center py-2 px-2">Night</th>
                <th className="text-center py-2 px-2">Score</th>
                <th className="text-center py-2 px-2">Status</th>
                <th className="text-center py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cities.map(c => (
                <tr key={c.city_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-700">{c.city_name}, {c.state_code}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      c.current_phase <= 3 ? "bg-sky-100 text-sky-700" :
                      c.current_phase <= 5 ? "bg-amber-100 text-amber-700" :
                      "bg-emerald-100 text-emerald-700"
                    }`}>P{c.current_phase}</span>
                  </td>
                  <td className="py-2 px-2 text-center text-slate-600">{c.sweep_night || 0}</td>
                  <td className="py-2 px-2 text-center">{(c.confidence_report?.overall_score || 0).toFixed(2)}</td>
                  <td className="py-2 px-2 text-center">
                    {c.confidence_report?.city_complete ? (
                      <span className="text-emerald-600 font-bold">Complete</span>
                    ) : c.active === false ? (
                      <span className="text-red-500 font-bold">Inactive</span>
                    ) : (
                      <span className="text-amber-600">Active</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => viewDetails(c.city_id)} className="p-1 rounded hover:bg-slate-200" title="View Details" data-testid={`view-details-${c.city_id}`}>
                        <Eye className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                      <button onClick={() => setConfirmAction({ type: "force-advance", cityId: c.city_id, label: `Force advance ${c.city_name}?` })} className="p-1 rounded hover:bg-amber-100" title="Force Advance" data-testid={`force-advance-${c.city_id}`}>
                        <SkipForward className="w-3.5 h-3.5 text-amber-500" />
                      </button>
                      <button onClick={() => setConfirmAction({ type: "reset", cityId: c.city_id, label: `Reset checkpoints for ${c.city_name}?` })} className="p-1 rounded hover:bg-blue-100" title="Reset Checkpoints" data-testid={`reset-cp-${c.city_id}`}>
                        <RotateCcw className="w-3.5 h-3.5 text-blue-500" />
                      </button>
                      <button onClick={() => setConfirmAction({ type: "remove", cityId: c.city_id, label: `Remove ${c.city_name} from pipeline? (data preserved)` })} className="p-1 rounded hover:bg-red-100" title="Remove City" data-testid={`remove-${c.city_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="confirm-dialog">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <p className="text-sm font-semibold text-slate-800 mb-4">{confirmAction.label}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-200 text-slate-700 hover:bg-slate-300" data-testid="confirm-cancel-btn">Cancel</button>
              <button
                onClick={() => handleAction(confirmAction.type, confirmAction.cityId)}
                disabled={!!actionLoading}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-action-btn"
              >
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* City Detail Modal */}
      {(detailCity || detailLoading) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="city-detail-modal">
          <div className="bg-white rounded-xl p-6 max-w-lg mx-4 shadow-xl max-h-[80vh] overflow-y-auto w-full">
            {detailLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
            ) : detailCity ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-800">{detailCity.city_name}, {detailCity.state_code}</h3>
                  <button onClick={() => setDetailCity(null)} className="p-1 rounded hover:bg-slate-100" data-testid="close-detail-btn">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-sky-50 rounded-lg p-3">
                    <p className="text-2xl font-black text-sky-700">{detailCity.businesses_discovered || 0}</p>
                    <p className="text-[10px] text-sky-600 uppercase">Businesses</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-2xl font-black text-amber-700">{detailCity.birthday_offers_found || 0}</p>
                    <p className="text-[10px] text-amber-600 uppercase">Birthday Offers</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-2xl font-black text-emerald-700">{detailCity.emails_collected || 0}</p>
                    <p className="text-[10px] text-emerald-600 uppercase">Emails</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-600 mb-2">Confidence Report</p>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Overall Score:</span>
                      <span className="font-bold">{(detailCity.confidence_report?.overall_score || 0).toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">City Complete:</span>
                      <span className={`font-bold ${detailCity.confidence_report?.city_complete ? "text-emerald-600" : "text-amber-600"}`}>
                        {detailCity.confidence_report?.city_complete ? "Yes" : "No"}
                      </span>
                    </div>
                    {detailCity.confidence_report?.per_category && Object.keys(detailCity.confidence_report.per_category).length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-500 mt-2 mb-1">Per-Category Scores:</p>
                        {Object.entries(detailCity.confidence_report.per_category).map(([cat, score]) => (
                          <div key={cat} className="flex justify-between text-[11px]">
                            <span className="text-slate-600 capitalize">{cat}</span>
                            <span className="font-mono text-slate-700">{typeof score === "number" ? score.toFixed(3) : JSON.stringify(score)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-600 mb-2">Phase Status</p>
                  <div className="grid grid-cols-7 gap-1">
                    {[1,2,3,4,5,6,7].map(i => {
                      const ps = detailCity.phase_status?.[`phase_${i}`] || {};
                      const st = ps.status || "pending";
                      return (
                        <div key={i} className={`rounded-lg p-2 text-center text-[10px] ${
                          st === "completed" ? "bg-emerald-100 text-emerald-700" :
                          st === "in_progress" ? "bg-sky-100 text-sky-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          <p className="font-bold">P{i}</p>
                          <p className="capitalize">{st}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
